import * as React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useRequests } from "@/hooks/useRequests";
import { Button } from "../ui/button";
import { Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Request } from "@/types/research.types";
import type { CreateRequestFormValues } from "@/schemas/request-gallery.schemas";

const ChainedRequestItem: React.FC<{
  name: string;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}> = ({ name, isFirst, isLast, onMoveUp, onMoveDown, onRemove }) => (
  <div className="flex items-center justify-between px-3 py-2 bg-primary-100 rounded-lg">
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-neutral-400 truncate">{name}</p>
    </div>
    <div className="flex items-center gap-3 ml-2">
      <Button
        variant="ghost"
        className="w-auto h-auto p-0"
        size="icon"
        onClick={onMoveUp}
        disabled={isFirst}
      >
        <ArrowUp className="size-3.5" />
      </Button>
      <Button
        variant="ghost"
        className="w-auto h-auto p-0"
        size="icon"
        onClick={onMoveDown}
        disabled={isLast}
      >
        <ArrowDown className="size-3.5" />
      </Button>
      <Button variant="ghost" className="w-auto h-auto p-0" size="icon" onClick={onRemove}>
        <Trash2 className="size-3.5 text-danger-200" />
      </Button>
    </div>
  </div>
);

export const ChainRequest = ({ type }: { type: "discuss" | "research" }) => {
  const { control } = useFormContext<CreateRequestFormValues>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "chain_with",
  });
  const [selectedValue, setSelectedValue] = React.useState<string>("");

  const { data: requestsData } = useRequests(type);
  const chainedRequestIds = fields.map((field) => field.requestId);

  const availableRequests =
    requestsData?.filter((request: Request) => !chainedRequestIds.includes(request.id)) ?? [];

  const handleSelectRequest = (requestId: string) => {
    if (requestId) {
      append({ requestId: requestId });
      setSelectedValue("");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Select onValueChange={handleSelectRequest} value={selectedValue}>
        <SelectTrigger className="w-full px-2 text-xs font-medium h-8">
          <SelectValue placeholder="+ Chain with" />
        </SelectTrigger>
        <SelectContent className="max-w-[260px]">
          {availableRequests.map((request: Request) => (
            <SelectItem key={request.id} value={request.id} className="text-xs">
              {request.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex flex-col gap-2">
        {fields.map((field, index) => {
          const req = requestsData?.find((r: Request) => r.id === field.requestId);
          if (!req) return null;
          return (
            <ChainedRequestItem
              key={field.id}
              name={req.name}
              isFirst={index === 0}
              isLast={index === fields.length - 1}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              onRemove={() => remove(index)}
            />
          );
        })}
      </div>
    </div>
  );
};
